import { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { AppUser, Notification } from '../../pm/types';
import { displayName, NOTIFICATIONS_DISPLAYED } from '../../pm/constants';
import {
  markAllNotificationsRead,
  markNotificationRead,
  subscribeAllUsers,
  subscribeMyNotifications,
} from '../../pm/firebase';
import { timeAgo } from '../../pm/timeAgo';
import { getCurrentUser } from '../../firebase';
import '../../pm/theme.css';

const NotificationCenter: React.FC = () => {
  const history = useHistory();
  const [currentUid, setCurrentUid] = useState<string>('');
  const [items, setItems] = useState<Notification[]>([]);
  const [users, setUsers] = useState<AppUser[]>([]);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    let active = true;
    getCurrentUser()
      .then((u: any) => { if (active) setCurrentUid(u?.uid || ''); })
      .catch(() => { if (active) setCurrentUid(''); });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (!currentUid) return;
    const unsub = subscribeMyNotifications(currentUid, setItems);
    return unsub;
  }, [currentUid]);

  useEffect(() => {
    if (!currentUid) return;
    const unsub = subscribeAllUsers(setUsers);
    return unsub;
  }, [currentUid]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  if (!currentUid) return null;

  const unreadCount = items.filter((n) => !n.read).length;
  const shown = items.slice(0, NOTIFICATIONS_DISPLAYED);

  async function handleClickItem(n: Notification) {
    if (!n.read) {
      try { await markNotificationRead(n.id); } catch (e) { /* ignore */ }
    }
    setOpen(false);
    history.push(`/Projects/${n.projectId}`);
  }

  async function handleMarkAll() {
    await markAllNotificationsRead(items);
  }

  return (
    <div className="pm-notif-floating pm-app">
      <div className="pm-notif-wrap" ref={wrapRef}>
        <button
          className="pm-notif-bell"
          onClick={() => setOpen((v) => !v)}
          aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ''}`}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
            <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
          </svg>
          {unreadCount > 0 ? <span className="pm-notif-badge">{unreadCount > 99 ? '99+' : unreadCount}</span> : null}
        </button>
        {open ? (
          <div className="pm-notif-menu">
            <div className="pm-notif-menu-header">
              <span>Notifications</span>
              {unreadCount > 0 ? (
                <button className="pm-link-button" onClick={handleMarkAll}>Mark all read</button>
              ) : null}
            </div>
            {shown.length === 0 ? (
              <div className="pm-notif-empty">You're all caught up</div>
            ) : (
              <div className="pm-notif-list">
                {shown.map((n) => {
                  const actorEmail = users.find((u) => u.uid === n.byUserId)?.email;
                  const actorName = displayName(actorEmail) || 'Someone';
                  const verb = n.type === 'assignment' ? 'assigned you a task' : 'mentioned you';
                  return (
                    <button
                      key={n.id}
                      className={`pm-notif-item ${n.read ? '' : 'unread'}`}
                      onClick={() => handleClickItem(n)}
                    >
                      <div className="pm-notif-item-body">
                        <div className="pm-notif-item-title">
                          <strong>{actorName}</strong> {verb}
                        </div>
                        <div className="pm-notif-item-preview">{n.preview}</div>
                        <div className="pm-notif-item-time">{timeAgo(n.createdAt)}</div>
                      </div>
                      {!n.read ? <span className="pm-notif-dot" aria-hidden="true" /> : null}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default NotificationCenter;
